import { useState } from 'react';
import { Alert, Button, Card, Group, List, Select, SimpleGrid, Stack, Switch, Text, Textarea, TextInput, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { validateAnalysisType, useAnalysisTypeMutations, type AnalysisType, type AnalysisTypeBody, type OutputDecl, type Slot, type Step } from '@/api/analyses';
import { errorMessage } from '@/api/errors';
import { useKinds } from '@/api/objects';
import { useRunTypes } from '@/api/runs';
import { brand } from '@/theme/theme';
import { SequenceEditor } from './SequenceEditor';

export interface AnalysisTypeEditorProps {
  initial?: AnalysisType;
  onSaved: (t: AnalysisType) => void;
}

const asSlots = (v: unknown): Slot[] => (Array.isArray(v) ? (v as Slot[]) : []);
const asOutputs = (v: unknown): OutputDecl[] => (Array.isArray(v) ? (v as OutputDecl[]) : []);
const asSteps = (v: unknown): Step[] => (Array.isArray(v) ? (v as Step[]) : []);
const asName = (v: unknown): { cs: string; en: string } => ({ cs: '', en: '', ...((v as object) ?? {}) }) as { cs: string; en: string };

function SlotList({ label, slots, kinds, onChange }: { label: string; slots: Slot[]; kinds: string[]; onChange: (s: Slot[]) => void }) {
  const { t } = useTranslation('analyses');
  return (
    <Stack gap="xs">
      <Text fz={12} fw={700} tt="uppercase" c={brand.textMuted}>
        {label}
      </Text>
      {slots.map((s, i) => (
        <Group key={i} gap="xs" align="flex-end">
          <TextInput label={t('editor.role')} value={s.role} onChange={(e) => onChange(slots.map((x, n) => (n === i ? { ...x, role: e.currentTarget.value } : x)))} w={160} />
          <Select label={t('editor.kind')} data={['*', ...kinds]} value={s.kind} onChange={(v) => v && onChange(slots.map((x, n) => (n === i ? { ...x, kind: v } : x)))} w={220} allowDeselect={false} />
          <Button size="compact-xs" color="red" variant="subtle" onClick={() => onChange(slots.filter((_, n) => n !== i))}>
            {t('editor.remove')}
          </Button>
        </Group>
      ))}
      <Button size="xs" variant="default" w="fit-content" onClick={() => onChange([...slots, { role: '', kind: kinds[0] ?? '*' }])}>
        {t('editor.addSlot')}
      </Button>
    </Stack>
  );
}

/** Compose an analysis type from run types; nothing saves until the server finds no problems (AN3). */
export function AnalysisTypeEditor({ initial, onSaved }: AnalysisTypeEditorProps) {
  const { t } = useTranslation('analyses');
  const { data: runTypes } = useRunTypes();
  const { data: kindRows } = useKinds();
  const { create, update } = useAnalysisTypeMutations();
  const kinds = (kindRows ?? []).map((k) => k.kind);
  const [key, setKey] = useState(initial?.key ?? '');
  const [name, setName] = useState(asName(initial?.name));
  const [description, setDescription] = useState(asName(initial?.description));
  const [required, setRequired] = useState<Slot[]>(asSlots(initial?.required_inputs));
  const [optional, setOptional] = useState<Slot[]>(asSlots(initial?.optional_inputs));
  const [outputs, setOutputs] = useState<OutputDecl[]>(asOutputs(initial?.outputs));
  const [steps, setSteps] = useState<Step[]>(asSteps(initial?.run_sequence));
  const [active, setActive] = useState(initial?.is_active ?? true);
  const [problems, setProblems] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);

  const body = (): AnalysisTypeBody => ({
    key,
    name,
    description,
    required_inputs: required,
    optional_inputs: optional,
    outputs,
    run_sequence: steps,
    is_active: active,
  });

  const validate = async () => {
    setBusy(true);
    try {
      const p = await validateAnalysisType(body());
      setProblems(p);
      return p;
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    const p = await validate();
    if (p.length) return;
    try {
      const saved = initial ? await update.mutateAsync({ key: initial.key, patch: body() }) : await create.mutateAsync(body());
      notifications.show({ color: 'forest', message: t('editor.saved') });
      onSaved(saved);
    } catch (err) {
      notifications.show({ color: 'red', message: errorMessage(err) });
    }
  };

  return (
    <Stack gap="lg">
      <Card>
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          <TextInput label={t('editor.key')} value={key} onChange={(e) => setKey(e.currentTarget.value)} disabled={!!initial} required />
          <Switch label={t('editor.active')} checked={active} onChange={(e) => setActive(e.currentTarget.checked)} mt={28} />
          <TextInput label={t('editor.nameCs')} value={name.cs} onChange={(e) => setName({ ...name, cs: e.currentTarget.value })} />
          <TextInput label={t('editor.nameEn')} value={name.en} onChange={(e) => setName({ ...name, en: e.currentTarget.value })} />
          <Textarea label={t('editor.descriptionCs')} value={description.cs} onChange={(e) => setDescription({ ...description, cs: e.currentTarget.value })} autosize minRows={2} />
          <Textarea label={t('editor.descriptionEn')} value={description.en} onChange={(e) => setDescription({ ...description, en: e.currentTarget.value })} autosize minRows={2} />
        </SimpleGrid>
      </Card>
      <Card>
        <Stack gap="md">
          <SlotList label={t('editor.required')} slots={required} kinds={kinds} onChange={setRequired} />
          <SlotList label={t('editor.optional')} slots={optional} kinds={kinds} onChange={setOptional} />
          <Stack gap="xs">
            <Text fz={12} fw={700} tt="uppercase" c={brand.textMuted}>
              {t('editor.outputs')}
            </Text>
            {outputs.map((o, i) => (
              <Group key={i} gap="xs" align="flex-end">
                <Select label={t('editor.kind')} data={['*', ...kinds]} value={o.kind} onChange={(v) => v && setOutputs(outputs.map((x, n) => (n === i ? { ...x, kind: v } : x)))} w={220} allowDeselect={false} />
                <Switch label={t('editor.deliverable')} checked={o.deliverable} onChange={(e) => setOutputs(outputs.map((x, n) => (n === i ? { ...x, deliverable: e.currentTarget.checked } : x)))} mb={6} />
                <Button size="compact-xs" color="red" variant="subtle" onClick={() => setOutputs(outputs.filter((_, n) => n !== i))}>
                  {t('editor.remove')}
                </Button>
              </Group>
            ))}
            <Button size="xs" variant="default" w="fit-content" onClick={() => setOutputs([...outputs, { kind: kinds[0] ?? '*', deliverable: true }])}>
              {t('editor.addOutput')}
            </Button>
          </Stack>
        </Stack>
      </Card>
      <Title order={3}>{t('editor.sequence')}</Title>
      <SequenceEditor steps={steps} runTypes={runTypes ?? []} slots={[...required, ...optional]} onChange={setSteps} />
      {problems && problems.length > 0 && (
        <Alert color="red" title={t('editor.problems')}>
          <List fz={13}>
            {problems.map((p, i) => (
              <List.Item key={i}>{p}</List.Item>
            ))}
          </List>
        </Alert>
      )}
      {problems && problems.length === 0 && <Alert color="forest">{t('editor.valid')}</Alert>}
      <Group justify="flex-end">
        <Button variant="default" loading={busy} onClick={() => void validate()}>
          {t('editor.validate')}
        </Button>
        <Button loading={create.isPending || update.isPending} disabled={!key} onClick={() => void save()}>
          {t('editor.save')}
        </Button>
      </Group>
    </Stack>
  );
}
